"use client";

import { MouseEvent } from "react";
import Image from "next/image";
import { Loading } from "react-vant";
import { AddOnProduct } from "@/types/product";
import { formatCurrency } from "@/utils/utils";
import styles from "@/styles/product-detail/addon-deals.module.scss";

interface Props {
  product: AddOnProduct;
  variant?: "ProductItem" | "ProductCardFullWidth";
  productItemSize?: "default" | "large";
  added?: boolean;
  adding?: boolean;
  onAdd?: (product: AddOnProduct, event: MouseEvent<HTMLDivElement>) => void;
  onViewDetail?: (product: AddOnProduct) => void;
}

export default function AddOnProductCard({
  product,
  variant = "ProductItem",
  productItemSize = "default",
  added = false,
  adding = false,
  onAdd,
  onViewDetail,
}: Props) {
  const handleAdd = (event: MouseEvent<HTMLDivElement>) => {
    if (added || adding) return;
    onAdd?.(product, event);
  };

  const handleViewDetail = () => {
    onViewDetail?.(product);
  };

  const renderAddIcon = (size: number) => {
    if (adding) {
      return <Loading color="#D90B17" />;
    }

    return (
      <Image
        src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/${added ? 'icon-add-on-success' : 'icon-add-on'}.svg`}
        alt="add"
        width={size}
        height={size}
      />
    );
  };

  if (variant === "ProductCardFullWidth") {
    return (
      <div className={styles["product-card-full"]}>
        <Image
          className={styles["product-card-image"]}
          src={product.filename}
          alt={product.name}
          width={127}
          height={127}
          onClick={handleViewDetail}
        />
        <div className={styles["product-card-info"]}>
          <div
            className={styles["product-card-name"]}
            onClick={handleViewDetail}
          >
            {product.name}
          </div>
          <div className={styles["product-card-label"]}>Add-on Price</div>
          <div className={styles["product-card-bottom"]}>
            <div className={styles["product-card-price"]}>
              <span className={styles["sale-price"]}>{formatCurrency(product.price)}</span>
              {product.normalPrice !== product.price && (
                <div className={styles["original-price-row"]}>
                  <span className={styles["original-price"]}>
                    {formatCurrency(product.normalPrice)}
                  </span>
                  <span className={styles["discount-badge"]}>
                    {product.promotion?.promotionRate}% OFF
                  </span>
                </div>
              )}
            </div>
            <div
              className={styles["add-btn"]}
              onClick={handleAdd}
            >
              {renderAddIcon(24)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLargeProductItem = productItemSize === "large";

  return (
    <div
      className={`${styles["addon-product-item"]} ${
        isLargeProductItem ? styles["addon-product-item-large"] : ""
      }`}
    >
      <Image
        className={styles["item-image"]}
        src={product.filename}
        alt={product.name}
        width={isLargeProductItem ? 132 : 75}
        height={isLargeProductItem ? 132 : 75}
        onClick={handleViewDetail}
      />

      <div className={styles["item-name"]} onClick={handleViewDetail}>
        {product.name}
      </div>

      <div className={styles["item-bottom"]}>
        <div className={styles["item-price"]}>
          <span className={styles["item-sale-price"]}>{formatCurrency(product.price)}</span>
          {product.normalPrice !== product.price && (
            <span className={styles["item-original-price"]}>
              {formatCurrency(product.normalPrice)}
            </span>
          )}
        </div>
        <div
          className={styles["add-btn"]}
          onClick={handleAdd}
        >
          {renderAddIcon(isLargeProductItem ? 32 : 20)}
        </div>
      </div>
    </div>
  );
}
